import { Component, Input, OnChanges, Output, EventEmitter, ChangeDetectorRef, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { Chart, ChartType, ChartDataset } from 'chart.js';
import { CartesianScaleOptions } from 'chart.js/auto';
import { NWMChartSettings } from './NWMChartSettings';
import { tooltipType } from "app/components/NWMCharts/NWMCharts.module";
import { AppConfig } from "app/app.config";

import { APIService } from 'app/services/api.service';
import { AuthenticationService } from 'app/services/auth.service';
import { LoaderService } from "app/services/loader.service";
import { BaseChartDirective } from 'ng2-charts';

type apiDataType = {
  Invested: number;
  Profit: number;
  Period: string;
}[];

@Component({
    selector: 'AICFChart',
    templateUrl: './nwmchart.component.html',
    styleUrls: ['./nwmchart.component.scss'],
    imports: [BaseChartDirective]
})

export class AICFChartComponent implements OnChanges {
  private api = inject(APIService);
  private auth = inject(AuthenticationService);
  private loaderService = inject(LoaderService);
  private config = inject(AppConfig);
  private ref = inject(ChangeDetectorRef);

  @Input() plan?: string;
  @Input() limit?: number;
  @Output() loaded : EventEmitter<boolean> = new EventEmitter();

  public dataLoaded = false;
  public chartData: ChartDataset[] = [
    { data: [], label: 'Net Amount Invested', pointRadius:0, pointHitRadius:0,
      backgroundColor: '#1D3A6A', borderColor: '#1D3A6A', fill: true },
    { data: [], label: 'Profit', pointRadius:0, pointHitRadius:0,
      backgroundColor: '#88A8A3', borderColor: '#88A8A3', fill: true },
  ];
  public chartLabels: string[] = [];
  public visibleLabels: string[] = [];
  public visibleLabelsStep = 1;
  public chartType: ChartType = 'line';
  public chartLegend = false;
  public tooltip: tooltipType = {visible:false, rows:[]};
  public chartSettings: NWMChartSettings;
  public width = 500;
  public height = 250;
  public valuationDate = '';

  constructor() {
    this.chartSettings = new NWMChartSettings();
  }

  ngOnChanges(): void {
    this.dataLoaded = false;
    this.tooltip.visible = false;
    this.chartSettings.options.plugins = {
      tooltip:{
        enabled: false,
        mode: 'index',
        intersect: false,
        external: (a)=>this.customTooltip(a.chart)
      }
    };
    this.chartSettings.options.scales!['x']!.ticks!.padding = this.limit ? 3 : 7;
    (this.chartSettings.options.scales!['x']! as CartesianScaleOptions).stacked! = false;
    (this.chartSettings.options.scales!['y']! as CartesianScaleOptions).stacked! = false;
    this.loadChart();
  }

  customTooltip(chart: Chart): void {
    setTimeout(()=>this.highlightPosition(chart));
    const tooltipModel = chart.tooltip!;
    this.tooltip.visible = tooltipModel.opacity !== 0;
    if (tooltipModel.body) {
      const i = tooltipModel.dataPoints[0].dataIndex;
      const invested = this.chartData[0].data[i] as number;
      const total = this.chartData[1].data[i] as number;
      const profit = total - invested;
      this.tooltip.rows = [
        { key: 'Period Ending:', value: this.chartLabels[i] },
        { key: this.chartData[0].label + ':', value: this.api.formatCurrency(invested) },
        { key: 'Profit:', value: this.api.formatCurrency(profit) },
        { key: 'Total:', value: this.api.formatCurrency(total), class: 'total' }
      ];
    }
    setTimeout(()=>{this.ref.detectChanges();});
  }

  highlightPosition(chart: Chart) {
    if (!chart.tooltip) return;
    const ctx = chart.ctx;
    let minY = Infinity;
    let minX = Infinity;
    ctx.save();
    chart.tooltip.dataPoints.forEach((p)=>{
      const {x,y} = p.element;
      if (y < minY) minY = y;
      if (x < minX) minX = x;
      ctx.beginPath();
      ctx.arc(x,y,1,0,2*Math.PI);
      ctx.strokeStyle = '#777';
      ctx.stroke();
    })

    ctx.beginPath();
    ctx.moveTo(minX , minY);
    ctx.lineTo(minX, chart.scales['y'].bottom);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.stroke();
    ctx.restore();
  }

  loadChart() {
    this.loaderService.display(true, 'AICF');
    const reRg = this.auth.getCurrentReRg()!;
    this.valuationDate = this.auth.getCurrentValuationDate();
    const planPath = this.api.getPlanPath(this.plan || '', reRg.IsPershing);

    this.api.get<apiDataType>(`${this.config.portfolio.portfolioSummaryTotalAmountInvestedUrl}/${reRg.ReRgId}/${this.valuationDate}/${planPath}`).subscribe(
      data => {
        this.loaded.emit(false);
        this.dataLoaded = false;
        setTimeout(()=>{this.dataLoaded = true});
        this.chartLabels = [];
        this.chartData[0].data = [];
        this.chartData[1].data = [];

        if (!data || data.length<2) return;
        if (this.limit) data = data.slice(-this.limit);
        this.visibleLabels = [];
        const dataLength = data.length;
        for (let i = 0; i < dataLength; i++) {
          const dataPoint = data[i];
          const year = dataPoint.Period.split('-')[0];
          if (this.visibleLabels.indexOf(year) == -1) this.visibleLabels.push(year);
          this.chartLabels.push(formatDate(dataPoint.Period, 'MMM yyyy', 'en-US'));
          const invested = dataPoint.Invested;
          const profit = dataPoint.Profit;
          const total = invested + profit;
          this.chartData[0].data.push(invested);
          this.chartData[1].data.push(total);
        }
        this.visibleLabelsStep = dataLength / (this.visibleLabels.length - 1);

        const valuationYear = this.valuationDate ? this.valuationDate.toNWMdate().getFullYear() : '';

        if (this.chartLabels[dataLength - 1].split(' ')[1] >= valuationYear) {
          this.chartLabels[dataLength - 1] = this.limit ? 'YTD': this.chartLabels[dataLength - 1].split(' ')[1] + ' YTD';
        }

        let tick = -1;
        this.chartSettings.options.scales!['x']!.ticks!.callback = (value, index) => {
          if (index == 0) tick = -1;
          if (index == 0 || index == dataLength - 1 || ((index + 1) % this.visibleLabelsStep < 1)) {
            if (index == dataLength - 1) return this.chartLabels[dataLength - 1];
            tick++;
            return this.visibleLabels[tick];
          }
          return null;
        }
        this.chartSettings.options.scales!['y']!.ticks!.callback = (value, index, ticks) =>
          this.chartSettings.getYAxisLabelToShow(ticks.map(t=>t.value), value as number);
        this.loaded.emit(true);
        this.loaderService.display(false, 'AICF');
      },
      () => {
        this.loaded.emit(false);
        this.loaderService.display(false, 'AICF');
      }
    );
  }
}
